import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import BackofficeLayout from "@/components/BackofficeLayout";
import AIAssistant from "@/components/AIAssistant";
import LimitReachedDialog from "@/components/LimitReachedDialog";
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import DashboardPage from "@/pages/DashboardPage";
import PropertiesPage from "@/pages/PropertiesPage";
import LeadsPage from "@/pages/LeadsPage";
import TeamPage from "@/pages/TeamPage";
import SettingsPage from "@/pages/SettingsPage";
import CalificacionesForecastPage from "@/pages/CalificacionesForecastPage";
import CapiPage from "@/pages/CapiPage";
import OpportunitiesPage from "@/pages/OpportunitiesPage";
import AgendaPage from "@/pages/AgendaPage";
import RulesPage from "@/pages/RulesPage";
import CommissionsPage from "@/pages/CommissionsPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ActivateAccountPage from "@/pages/ActivateAccountPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import SuperAdminsPage from "@/pages/SuperAdminsPage";
import Suspensiones from "@/pages/backoffice/Suspensiones";
import TiposDeUsuario from "@/pages/backoffice/TiposDeUsuario";
import AIAgentPage from "@/pages/AIAgentPage";
import AgencyPage from "@/pages/AgencyPage";
import DeveloperLandingPage from "@/pages/DeveloperLandingPage";
import GuardsPage from "@/pages/GuardsPage";
import DevelopmentsPage from "@/pages/DevelopmentsPage";
import MassCampaignsPage from "@/pages/MassCampaignsPage";
import RecipesPage from "@/pages/RecipesPage";
import AcademyPage from "@/pages/AcademyPage";
import MarketplacePage from "@/pages/MarketplacePage";
import MarketplacePropertyPage from "@/pages/MarketplacePropertyPage";
import MarketplaceDevelopmentPage from "@/pages/MarketplaceDevelopmentPage";
import SellerMobileApp from "@/pages/SellerMobileApp";
import CorreoCampanasPage from "@/pages/CorreoCampanasPage";
import CarDealerPage from "@/pages/CarDealerPage";
import CarBuilderPage from "@/pages/CarBuilderPage";
import ProductsPage from "@/pages/ProductsPage";
import BrandsPage from "@/pages/BrandsPage";
import CarSitePublicPage from "@/pages/CarSitePublicPage";
import DevBuilderListPage from "@/pages/DevBuilderListPage";
import DevBuilderPage from "@/pages/DevBuilderPage";
import DevSitePublicPage from "@/pages/DevSitePublicPage";
import EmailConfig from "@/pages/backoffice/EmailConfig";
import AdTracking from "@/pages/backoffice/AdTracking";
import DevTickets from "@/pages/backoffice/DevTickets";
import AICampaignIndependentPage from "@/pages/AICampaignIndependentPage";
import GroupsPage from "@/pages/GroupsPage";
import FileLibraryPage from "@/pages/FileLibraryPage";
import TeamConfigPage from "@/pages/TeamConfigPage";
import WhiteLabelPage from "@/pages/WhiteLabelPage";
import WhatsAppPage from "@/pages/WhatsAppPage";
import PublicLandingPage from "@/pages/PublicLandingPage";
import TerminosPage from "@/pages/TerminosPage";
import AvisoPrivacidadPage from "@/pages/AvisoPrivacidadPage";
import ApiGeneratorPage from "@/pages/ApiGeneratorPage";
import ApiHubPage from "@/pages/ApiHubPage";
import ApiDocsPage from "@/pages/ApiDocsPage";
import VirtualCardPage from "@/pages/VirtualCardPage";
import VCardPublicPage from "@/pages/VCardPublicPage";

import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { primeraRutaPermitida, tieneSaPerm } from "@/lib/backofficeNav";

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  return user ? children : <Navigate to="/login" />;
}

function SuperAdminRoute({ children }) {
  const { user, loading, impersonating } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" />;

  const canAccessBackoffice =
    !impersonating &&
    user.role === 'superadmin' &&
    (user.company_id === 'dealider-system' || user.platform_role === 'owner' || user.platform_role === 'superadmin');

  return canAccessBackoffice ? children : <Navigate to="/dashboard" replace />;
}

function SaPermRoute({ perm, children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!tieneSaPerm(user, perm)) {
    return <Navigate to={primeraRutaPermitida(user)} replace />;
  }
  return children;
}

// Roles permitidos cuando una ruta es adminOnly (bloquea vendor/guard).
// Fail-closed: solo estos roles pasan; cualquier otro (incl. null/desconocido) rebota.
const MODULE_ADMIN_ROLES = ['owner', 'manager', 'admin', 'superadmin'];

function ModuleRoute({ moduleKey, adminOnly, children }) {
  const { user, loading, impersonating } = useAuth();
  if (loading) return null;
  // Gate por ROL (adminOnly): misma intención que el menú (oculta a vendor/guard),
  // pero fail-closed — redirige salvo que el rol esté explícitamente permitido.
  if (adminOnly && !MODULE_ADMIN_ROLES.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  // Gate por PLAN/LICENCIA (moduleKey).
  const modules = user?.allowed_modules || user?.license_profile?.included_modules || {};
  const PADRE_DE = {
    agenda: 'leads',
    file_library: 'ai_agent',
    waba: 'settings',
    api_hub: 'settings',
    landing: 'settings',
    cem_ratings: 'settings',
  };
  const SA_SIEMPRE = ['settings', 'waba', 'api_hub', 'landing', 'cem_ratings', 'capi'];
  const saSiempre = impersonating && moduleKey && SA_SIEMPRE.includes(moduleKey);
  const apagado = moduleKey && (
    modules[moduleKey] === false
    || (modules[moduleKey] === undefined && PADRE_DE[moduleKey] && modules[PADRE_DE[moduleKey]] === false)
  );
  if (apagado && !saSiempre) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  const { user, loading, impersonating } = useAuth();
  if (loading) return null;

  const canAccessBackoffice =
    !impersonating &&
    user?.role === 'superadmin' &&
    (user?.company_id === 'dealider-system' || user?.platform_role === 'owner' || user?.platform_role === 'superadmin');

  const defaultPath = canAccessBackoffice ? primeraRutaPermitida(user) : '/dashboard';
  const isMarketplaceHost = typeof window !== 'undefined' && window.location.hostname.startsWith('propiedades.');

  if (isMarketplaceHost) {
    return (
      <Routes>
        <Route path="/" element={<MarketplacePage />} />
        <Route path="/propiedad/:companySlug/:propertyId" element={<MarketplacePropertyPage />} />
        <Route path="/desarrollo/:companySlug/:developmentId" element={<MarketplaceDevelopmentPage />} />
        <Route path="/terminos" element={<TerminosPage />} />
        <Route path="/privacidad" element={<AvisoPrivacidadPage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to={defaultPath} /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to={defaultPath} /> : <LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/activar-cuenta" element={<ActivateAccountPage />} />
      <Route path="/terminos" element={<TerminosPage />} />
      <Route path="/privacidad" element={<AvisoPrivacidadPage />} />
      <Route path="/developers" element={<ApiDocsPage />} />
      <Route path="/car-sites/:slug" element={<CarSitePublicPage />} />
      <Route path="/dev-sites/:slug" element={<DevSitePublicPage />} />
      <Route path="/l/:slug" element={<PublicLandingPage />} />
      <Route path="/vcard/:slug" element={<VCardPublicPage />} />
      <Route path="/mobile" element={<PrivateRoute><SellerMobileApp /></PrivateRoute>} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/marketplace/propiedad/:companySlug/:propertyId" element={<MarketplacePropertyPage />} />
      <Route path="/marketplace/desarrollo/:companySlug/:developmentId" element={<MarketplaceDevelopmentPage />} />
      <Route path="/a/:slug" element={<AgencyPage />} />
      <Route path="/d/:slug" element={<DeveloperLandingPage />} />
      <Route path="/propiedad/:companySlug/:propertyId" element={<MarketplacePropertyPage />} />
      <Route path="/desarrollo/:companySlug/:developmentId" element={<MarketplaceDevelopmentPage />} />

      {/* BackOffice routes - Super Admin only, separate layout */}
      <Route path="/backoffice" element={<SuperAdminRoute><BackofficeLayout /></SuperAdminRoute>}>
        <Route index element={<SaPermRoute perm="companies"><SuperAdminPage initialTab="companies" /></SaPermRoute>} />
        <Route path="uso-ia" element={<SaPermRoute perm="analytics"><SuperAdminPage initialTab="ai" /></SaPermRoute>} />
        <Route path="plataforma" element={<Navigate to="/backoffice/integraciones" replace />} />
        <Route path="planes" element={<SaPermRoute perm="plans"><SuperAdminPage initialTab="dynplans" /></SaPermRoute>} />
        <Route path="propiedades" element={<SaPermRoute perm="properties"><SuperAdminPage initialTab="properties" /></SaPermRoute>} />
        <Route path="reportes" element={<SaPermRoute perm="reports"><SuperAdminPage initialTab="reports" /></SaPermRoute>} />
        <Route path="superadmins" element={<SaPermRoute perm="superadmins"><SuperAdminsPage /></SaPermRoute>} />
        <Route path="integraciones" element={<SaPermRoute perm="integrations"><SuperAdminPage initialTab="backoffice" /></SaPermRoute>} />
        <Route path="email-config" element={<SaPermRoute perm="integrations"><EmailConfig /></SaPermRoute>} />
        <Route path="tracking-publicitario" element={<SaPermRoute perm="integrations"><AdTracking /></SaPermRoute>} />
        <Route path="proyectos" element={<SaPermRoute perm="projects"><DevTickets /></SaPermRoute>} />
        <Route path="academy" element={<SaPermRoute perm="academy"><AcademyPage isSuperAdmin={true} /></SaPermRoute>} />
        <Route path="bitacora" element={<SaPermRoute perm="audit"><SuperAdminPage initialTab="audit" /></SaPermRoute>} />
        <Route path="suspensiones" element={<SaPermRoute perm="audit"><Suspensiones /></SaPermRoute>} />
        <Route path="tipos-usuario" element={<SaPermRoute perm="superadmins"><TiposDeUsuario /></SaPermRoute>} />
        <Route path="user-tenants" element={<SaPermRoute perm="superadmins"><TiposDeUsuario /></SaPermRoute>} />
      </Route>

      {/* Legacy admin route redirects */}
      <Route path="/admin" element={canAccessBackoffice ? <Navigate to="/backoffice" replace /> : <Navigate to="/dashboard" replace />} />
      <Route path="/admin/*" element={canAccessBackoffice ? <Navigate to="/backoffice" replace /> : <Navigate to="/dashboard" replace />} />
      <Route path="/superadmin" element={canAccessBackoffice ? <Navigate to="/backoffice" replace /> : <Navigate to="/dashboard" replace />} />

      {/* CRM routes - Licensees (admin + vendor) with DashboardLayout */}
      <Route path="/" element={<PrivateRoute><DashboardLayout /></PrivateRoute>}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="properties" element={<ModuleRoute moduleKey="properties"><PropertiesPage /></ModuleRoute>} />
        <Route path="leads" element={<ModuleRoute moduleKey="leads"><LeadsPage /></ModuleRoute>} />
        <Route path="opportunities" element={<ModuleRoute moduleKey="opportunities"><OpportunitiesPage /></ModuleRoute>} />
        <Route path="agenda" element={<ModuleRoute moduleKey="agenda"><AgendaPage /></ModuleRoute>} />
        <Route path="ai-agent" element={<ModuleRoute moduleKey="ai_agent"><AIAgentPage /></ModuleRoute>} />
        <Route path="commissions" element={<ModuleRoute moduleKey="commissions"><CommissionsPage /></ModuleRoute>} />
        <Route path="team-config" element={<TeamConfigPage />} />
        <Route path="guards" element={<Navigate to="/team-config?tab=guardias" replace />} />
        <Route path="developments" element={<DevelopmentsPage />} />
        <Route path="metricas" element={<Navigate to="/dashboard" replace />} />
        <Route path="campanas-masivas" element={<ModuleRoute moduleKey="mass_campaigns" adminOnly><MassCampaignsPage /></ModuleRoute>} />
        {/* La pantalla vieja de Mailings se retiro en ago-2026. La ruta no se
            borra: redirige, para que quien la tenga en favoritos no se tope
            con una pantalla en blanco. */}
        <Route path="marketing" element={<Navigate to="/correo" replace />} />
        {/* La pantalla nueva vive EN PARALELO a la vieja. Cuando este
            completa se cambia el menu y la vieja se retira con su endpoint.
            Asi en ningun momento hay una pantalla rota en produccion. */}
        <Route path="correo" element={<ModuleRoute moduleKey="marketing"><CorreoCampanasPage /></ModuleRoute>} />
        <Route path="campana-ia-independiente" element={<ModuleRoute moduleKey="ai_independent_campaigns"><AICampaignIndependentPage /></ModuleRoute>} />
        <Route path="logicas-recetas" element={<ModuleRoute moduleKey="comm_logics"><RecipesPage /></ModuleRoute>} />
        <Route path="car-dealer" element={<ModuleRoute moduleKey="car_dealer"><CarDealerPage /></ModuleRoute>} />
        <Route path="car-builder" element={<ModuleRoute moduleKey="car_builder"><CarBuilderPage /></ModuleRoute>} />
        <Route path="dev-builder" element={<ModuleRoute moduleKey="dev_builder"><DevBuilderListPage /></ModuleRoute>} />
        <Route path="dev-builder/:slug" element={<ModuleRoute moduleKey="dev_builder"><DevBuilderPage /></ModuleRoute>} />
        <Route path="products" element={<ModuleRoute moduleKey="products"><ProductsPage /></ModuleRoute>} />
        <Route path="brands" element={<ModuleRoute moduleKey="brands"><BrandsPage /></ModuleRoute>} />
        <Route path="rules" element={<Navigate to="/opportunities?seccion=reglas" replace />} />
        <Route path="groups" element={<Navigate to="/team-config?tab=grupos" replace />} />
        <Route path="knowledge-base" element={<Navigate to="/settings" replace />} />
        <Route path="biblioteca" element={<ModuleRoute moduleKey="file_library"><FileLibraryPage /></ModuleRoute>} />
        <Route path="white-label" element={<ModuleRoute moduleKey="landing" adminOnly><WhiteLabelPage /></ModuleRoute>} />
        <Route path="whatsapp" element={<ModuleRoute moduleKey="waba" adminOnly><WhatsAppPage /></ModuleRoute>} />
        <Route path="endpoint-builder" element={<Navigate to="/dev-hub?tab=builder" replace />} />
        <Route path="dev-hub" element={<ModuleRoute moduleKey="api_hub" adminOnly><ApiHubPage /></ModuleRoute>} />
        <Route path="mi-tarjeta-virtual" element={<VirtualCardPage />} />
        <Route path="team" element={<Navigate to="/team-config?tab=equipo" replace />} />
        <Route path="settings" element={<ModuleRoute moduleKey="settings" adminOnly><SettingsPage /></ModuleRoute>} />
        <Route path="settings/calificaciones-forecast" element={<ModuleRoute moduleKey="cem_ratings" adminOnly><CalificacionesForecastPage /></ModuleRoute>} />
        <Route path="settings/capi" element={<ModuleRoute moduleKey="capi" adminOnly><CapiPage /></ModuleRoute>} />
        <Route path="academy" element={<AcademyPage isSuperAdmin={false} />} />
      </Route>

      <Route path="*" element={user ? <Navigate to={defaultPath} /> : <Navigate to="/" />} />
    </Routes>
  );
}

function AIWrapper() {
  const { user } = useAuth();
  if (!user || user.role === 'superadmin') return null;
  return <AIAssistant />;
}

function ToasterWrapper() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" richColors closeButton />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <AIWrapper />
          <LimitReachedDialog />
          <ToasterWrapper />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
